import { Map as ImmutableMap } from 'immutable';
import { v4 as uuid } from 'uuid';

import { selectShouldHideBusinessFromAiChat } from 'app-components/flux/ai-chat/aiChatSelectors';
import { getBusinessCoaIdsSet } from 'app-components/flux/categories/categoriesSelectors';
import { createDialog } from 'app-components/flux/root-ui/rootUiActions';
import { mkRootUiData } from 'app-components/flux/root-ui/rootUiTypes';
import * as scheduledTransactionsSelectors from 'app-components/flux/scheduled-transactions/scheduledTransactionsSelectors';
import { getLogger } from 'app-components/utils/core';
import tracker from 'app-components/utils/core/tracker';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { safeParse } from '../../aiChatUtils';
import { returnToolResponse } from '../llmToolsFramework';
import type { ToolMessage } from '../llmToolsTypes';
import { findAccountIdByName, findCategoryIdByName } from '../utils';

const log = getLogger('llmRecurringTools');

export const recurringTools = {
  list_recurring_series: {
    definition: {
      name: 'list_recurring_series',
      description: `Retrieves all recurring series (scheduled transactions) including bills, income, and transfers. Returns a comprehensive list with details about payee, amount, frequency, next due date, account, category, isActive status, and more. The isActive field indicates whether the recurring series is active (true) or inactive/completed (false) - inactive bills should not be counted as current obligations. Use this to show the user their recurring bills and income, or to understand what recurring transactions are set up. NEVER show IDs (series ID, account ID, category ID) to the user - always display names only. Be concise and brief in your response.`,
      parameters: {
        type: 'object' as const,
        properties: {
          type: {
            type: 'string' as const,
            description: 'Filter by type: "bills" for expenses/bills only, "income" for income only, or omit for all',
            enum: ['bills', 'income'],
          },
          accountId: {
            type: 'string' as const,
            description: 'Filter by specific account ID',
          },
        },
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      if (!store) {
        log.error('llmTools/recurring: Store is not available');
        return returnToolResponse(msg, { recurring: [], error: 'Store not available' });
      }

      const args = safeParse(msg.arguments) || {};
      const state = store.getState() as RootState;

      const { type, accountId } = args;

      log.debug('llmTools/recurring: Fetching recurring series', args);

      const allScheduledTransactions = scheduledTransactionsSelectors.getUserVerifiedScheduledTransactions(state);

      if (!allScheduledTransactions || allScheduledTransactions.size === 0) {
        return returnToolResponse(msg, { recurring: [], message: 'No recurring series found.' });
      }

      let filtered = allScheduledTransactions;

      if (accountId) {
        filtered = filtered.filter((st) => st.transaction?.accountId === accountId);
      }

      let resultList;

      if (type === 'bills' || type === 'income') {
        const billsAndIncome = scheduledTransactionsSelectors.getBillsAndIncomeUserVerified(state);
        const targetSet = type === 'bills' ? billsAndIncome.get('bills') : billsAndIncome.get('income');

        if (!targetSet) {
          return returnToolResponse(msg, { recurring: [], message: `No ${type} found.` });
        }

        resultList = targetSet.toArray().filter((st: any) => !accountId || st.transaction?.accountId === accountId);
      } else {
        resultList = filtered.valueSeq().toArray();
      }

      const recurringList = resultList.map((st: any) => ({
        id: st.id,
        payee: st.transaction?.payee,
        amount: st.transaction?.amount,
        dueOn: st.dueOn,
        nextDueOn: st.overrideNextDueOn || st.dueOn,
        accountId: st.transaction?.accountId,
        category: st.transaction?.coa?.type === 'CATEGORY' ? st.transaction.coa.id : 'Uncategorized',
        frequency: st.recurrence?.alias || st.recurrence?.frequency,
        reminderDays: st.reminderDays,
        autoAcceptDays: st.autoAcceptDays,
        type: st.type,
        isUserVerified: st.isUserVerified,
        isActive: !st.isCompleted,
        billPresentmentAccountId: st.billPresentmentAccountId,
        memo: st.transaction?.memo,
      }));

      log.debug('llmTools/recurring: Retrieved recurring series:', recurringList.length);

      return returnToolResponse(msg, {
        success: true,
        count: recurringList.length,
        recurring: recurringList,
      });
    },
  },

  get_recurring_details: {
    definition: {
      name: 'get_recurring_details',
      description: `Get detailed information about a specific recurring series (scheduled transaction) by ID. Returns all available fields including recurrence pattern, reminder settings, auto-adjust settings, isActive status, and more. The isActive field indicates whether the recurring series is active (true) or inactive/completed (false). NEVER show IDs (series ID, account ID, category ID) to the user - always display names only. Be concise and brief in your response.`,
      parameters: {
        type: 'object' as const,
        properties: {
          seriesId: {
            type: 'string' as const,
            description: 'The unique ID of the recurring series to retrieve',
          },
        },
        required: ['seriesId'],
      },
    },
    handler: (msg: ToolMessage) => {
      tracker.track(tracker.events.AIChatRecurring);

      if (!store) {
        log.error('llmTools/recurring: Store is not available');
        return returnToolResponse(msg, { success: false, error: 'Store not available' });
      }

      const args = safeParse(msg.arguments) || {};
      const state = store.getState() as RootState;

      const { seriesId } = args;

      if (!seriesId) {
        return returnToolResponse(msg, {
          success: false,
          error: 'Series ID is required',
        });
      }

      log.debug('llmTools/recurring: Getting recurring series details', { seriesId });

      const scheduledTransaction = scheduledTransactionsSelectors.getScheduledTransactionById(state, seriesId);

      if (!scheduledTransaction) {
        return returnToolResponse(msg, {
          success: false,
          error: `Recurring series not found with ID: ${seriesId}`,
        });
      }

      const details = {
        id: scheduledTransaction.id,
        payee: scheduledTransaction.transaction?.payee,
        amount: scheduledTransaction.transaction?.amount,
        dueOn: scheduledTransaction.dueOn,
        nextDueOn: scheduledTransaction.overrideNextDueOn || scheduledTransaction.dueOn,
        nextAmount: scheduledTransaction.overrideNextAmount,
        accountId: scheduledTransaction.transaction?.accountId,
        category:
          scheduledTransaction.transaction?.coa?.type === 'CATEGORY'
            ? scheduledTransaction.transaction.coa.id
            : 'Uncategorized',
        memo: scheduledTransaction.transaction?.memo,
        type: scheduledTransaction.type,
        recurrence: scheduledTransaction.recurrence
          ? {
              frequency: scheduledTransaction.recurrence.frequency,
              interval: scheduledTransaction.recurrence.interval,
              alias: scheduledTransaction.recurrence.alias,
              endOn: scheduledTransaction.recurrence.endOn,
              byMonthDay: scheduledTransaction.recurrence.byMonthDay,
              byDay: scheduledTransaction.recurrence.byDay,
              bySetPos: scheduledTransaction.recurrence.bySetPos,
            }
          : null,
        reminderDays: scheduledTransaction.reminderDays,
        autoAcceptDays: scheduledTransaction.autoAcceptDays,
        autoSendDays: scheduledTransaction.autoSendDays,
        autoAdjustAmount: scheduledTransaction.autoAdjustAmount,
        autoAdjustDueOn: scheduledTransaction.autoAdjustDueOn,
        isUserVerified: scheduledTransaction.isUserVerified,
        isActive: !scheduledTransaction.isCompleted,
        billPresentmentAccountId: scheduledTransaction.billPresentmentAccountId,
        paymentProvider: scheduledTransaction.paymentProvider,
        createdAt: scheduledTransaction.createdAt,
        modifiedAt: scheduledTransaction.modifiedAt,
      };

      log.debug('llmTools/recurring: Recurring series details retrieved', { seriesId });

      return returnToolResponse(msg, {
        success: true,
        series: details,
      });
    },
  },

  list_unconnected_bills: {
    definition: {
      name: 'list_unconnected_bills',
      description: `Retrieves all bills that are not connected to online bill presentment (e-bills). These are bills that could potentially be connected to receive automatic bill information. Returns bills that do not have a billPresentmentAccountId. NEVER show IDs (series ID, account ID, category ID) to the user - always display names only. Be concise and brief in your response.`,
      parameters: {
        type: 'object' as const,
        properties: {},
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      tracker.track(tracker.events.AIChatListBills);

      if (!store) {
        log.error('llmTools/recurring: Store is not available');
        return returnToolResponse(msg, { bills: [], error: 'Store not available' });
      }

      const state = store.getState() as RootState;

      log.debug('llmTools/recurring: Fetching unconnected bills');

      const unconnectedBills = scheduledTransactionsSelectors.getUnconnectedBills(state);

      if (!unconnectedBills || unconnectedBills.size === 0) {
        return returnToolResponse(msg, {
          success: true,
          bills: [],
          message: 'No unconnected bills found. All bills are either connected or categorized as income.',
        });
      }

      const billsList = unconnectedBills.valueSeq().reduce((acc: any[], st: any) => {
        acc.push({
          id: st.id,
          payee: st.transaction?.payee,
          amount: st.transaction?.amount,
          dueOn: st.dueOn,
          accountId: st.transaction?.accountId,
          category: st.transaction?.coa?.type === 'CATEGORY' ? st.transaction.coa.id : 'Uncategorized',
          frequency: st.recurrence?.alias || st.recurrence?.frequency,
          isActive: !st.isCompleted,
        });
        return acc;
      }, []);

      log.debug('llmTools/recurring: Retrieved unconnected bills:', billsList.length);

      return returnToolResponse(msg, {
        success: true,
        count: billsList.length,
        bills: billsList,
        message: `Found ${billsList.length} unconnected bill${billsList.length === 1 ? '' : 's'} that could be connected to online bill presentment.`,
      });
    },
  },

  upsert_recurring: {
    definition: {
      name: 'upsert_recurring',
      description: `Create or update a recurring series (scheduled transaction). Opens a dialog for the user to review and confirm. All fields are optional.

If seriesId is provided, updates that series. Otherwise creates a new one.
Use list_recurring_series or get_recurring_details first to find an existing series if the user wants to modify one.

- seriesId: ID of existing series to update (from list_recurring_series)
- name: Series name/payee (e.g., "Netflix", "Rent Payment")
- amount: Transaction amount (negative for expenses/bills, positive for income)
- type: "bill" or "income"
- dueOn: Next due date (YYYY-MM-DD format)
- account: Account name for the transaction
- category: Category name

Be concise and brief in your response.`,
      parameters: {
        type: 'object' as const,
        properties: {
          seriesId: {
            type: 'string' as const,
            description: 'ID of existing series to update (omit to create new)',
          },
          name: {
            type: 'string' as const,
            description: 'Series name/payee',
          },
          amount: {
            type: 'number' as const,
            description: 'Amount (negative for expense/bill, positive for income)',
          },
          type: {
            type: 'string' as const,
            enum: ['bill', 'income'],
            description: 'Type of recurring series',
          },
          dueOn: {
            type: 'string' as const,
            description: 'Next due date (YYYY-MM-DD)',
          },
          account: {
            type: 'string' as const,
            description: 'Account name',
          },
          category: {
            type: 'string' as const,
            description: 'Category name',
          },
        },
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const args = safeParse(msg.arguments) || {};
      const { seriesId, name, amount, type, dueOn, account, category } = args;

      const state = store?.getState() as RootState;
      const existingSeries = seriesId
        ? scheduledTransactionsSelectors.getScheduledTransactionById(state, seriesId)
        : undefined;
      const operation = existingSeries ? 'update' : 'create';

      const categoryId = category ? findCategoryIdByName(state, category) : undefined;
      const accountId = account ? findAccountIdByName(state, account) : undefined;

      if (category && !categoryId) {
        log.warn('upsert_recurring: Category not found by name', { category });
      }
      if (account && !accountId) {
        log.warn('upsert_recurring: Account not found by name', { account });
      }

      if (selectShouldHideBusinessFromAiChat(state)) {
        const businessCoaIds = getBusinessCoaIdsSet(state);
        const existingCoa = existingSeries?.transaction?.coa;
        const existingCategoryId = existingCoa?.type === 'CATEGORY' ? existingCoa.id : undefined;
        if (
          (categoryId && businessCoaIds.has(categoryId)) ||
          (existingCategoryId && businessCoaIds.has(existingCategoryId))
        ) {
          return returnToolResponse(msg, {
            success: false,
            error:
              'Recurring series with business categories are not supported via chat. Use the dedicated business features in Quicken instead.',
          });
        }
      }

      log.debug('upsert_recurring:', { operation, seriesId, category, categoryId, account, accountId });

      return new Promise((resolve) => {
        const callback = (result: { success: boolean; seriesId?: string; error?: string }) => {
          if (result.success) {
            log.debug('upsert_recurring: Success', { operation });
            resolve(
              returnToolResponse(msg, { success: true, message: `Recurring series ${operation}d successfully.` }),
            );
          } else {
            log.error('upsert_recurring: Failed', { error: result.error });
            resolve(
              returnToolResponse(msg, {
                success: false,
                error: result.error || `Failed to ${operation} recurring series`,
              }),
            );
          }
        };

        const data = { seriesId, name, amount, type, dueOn, accountId, categoryId };

        const dialogData = mkRootUiData({
          type: 'LLM_RECURRING',
          id: `llm-recurring-${operation}-${uuid()}`,
          visible: true,
          allowNesting: false,
          props: ImmutableMap({ operation, data, callback }),
        });

        log.debug('upsert_recurring: Dispatching dialog');
        store?.dispatch(createDialog(dialogData));
      });
    },
  },
};
